import React, { forwardRef, memo, useCallback } from 'react';
import styled from 'styled-components';
import theme from '@styles/theme';
import { darken, lighten } from '../../lib/polished';

type StyledButtonProps = {
	width?: number;
	height?: number;
	fontSize?: number;
	fontWeight?: string;
	fontFamily?: string;
	fontColor?: string;
	padding?: string;
	margin?: string;
	border?: string;
	borderRadius?: number;
	bgColor?: string;
	cursor?: string;
	title?: string;
	disabled?: boolean;
	type?: 'button' | 'submit' | 'reset';
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	children?: React.ReactNode;
};

type ButtonStyledProps = {
	$width?: number;
	$height?: number;
	$fontSize?: number;
	$fontFamily?: string;
	$fontWeight?: string;
	$fontColor?: string;
	$padding?: string;
	$border?: string;
	$borderRadius?: number;
	$margin?: string;
	$bgColor?: string;
	$cursor?: string;
};

const StyledButton = forwardRef<HTMLButtonElement, StyledButtonProps>(({
		width,
		height = 50,
		fontSize = 16,
		fontWeight,
		fontFamily = 'PretendardSemiBold',
		fontColor = theme.colors.blackColor,
		padding = '0 12px',
		margin = '0',
		border = `1px solid ${theme.colors.ultraLightGrayBorder}`,
		borderRadius = 4,
		bgColor = theme.colors.whiteColor,
		cursor = 'pointer',
		title = '',
		disabled = false,
		type = 'button',
		onClick,
		children
	}, ref) => {
		const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
			if (!disabled && onClick) {
				onClick(e);
			}
		}, [disabled, onClick]);
		
		return (
			<Button
				type={type}
				$width={width}
				$height={height}
				$fontSize={fontSize}
				$fontWeight={fontWeight}
				$fontFamily={fontFamily}
				$fontColor={fontColor}
				$padding={padding}
				$margin={margin}
				$border={border}
				$borderRadius={borderRadius}
				$bgColor={bgColor}
				$cursor={cursor}
				ref={ref}
				onClick={handleClick}
				disabled={disabled}
				aria-disabled={disabled}>
                {children || title}
            </Button>
		);
	}
);

StyledButton.displayName = 'StyledButton';

export default memo(StyledButton);

const Button = styled.button<ButtonStyledProps>`
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  height: ${({ $height }) => $height}px;
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-family: ${({ $fontFamily }) => $fontFamily};
  font-weight: ${({ $fontWeight }) => $fontWeight};
  color: ${({ $fontColor }) => $fontColor};
  padding: ${({ $padding }) => $padding};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: ${({ $border }) => $border};
  border-radius: ${({ $borderRadius }) => $borderRadius}px;
  margin: ${({ $margin }) => $margin};
  background-color: ${({ $bgColor }) => $bgColor};
  cursor: ${({ $cursor }) => $cursor};
  transition: background-color 0.2s ease, transform 0.1s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background-color: ${({ $bgColor }) =>
            $bgColor === '#fff' || $bgColor === theme.colors.whiteColor
                    ? lighten(0.01, theme.colors.lightGrayHoverColor)
                    : $bgColor === '#000' || $bgColor === theme.colors.blackColor
                    ? lighten(0.25, $bgColor ?? theme.colors.blackColor)
                    : lighten(0.03, $bgColor ?? theme.colors.blackColor)};
  }

  &:not(:disabled):active {
    background-color: ${({ $bgColor }) => darken(0.05, $bgColor ?? theme.colors.blackColor)};
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.activeBlue};
    outline-offset: 2px;
  }
`;
